// src/pages/analytics/payables/QboDiffTab.tsx
//
// Advantage vs QuickBooks Online, document by document (GET /api/qbo/diff,
// agent 3.24.0+, QBO tenants). Bills (apinhead ↔ Bill / VendorCredit) or
// invoices (InHead ↔ Invoice / CreditMemo). Filter chips per difference type;
// "matched by" shows how each pair was found.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GitCompare, RefreshCw, Search } from 'lucide-react';
import { apiV2 as api } from '../../../services/apiV2';
import DateField from '../../../components/DateField';
import type { QboDiffFlag, QboDiffRow } from '../../../types/payables';
import { exportCsv, type CsvColumns } from '../../../lib/csvExport';
import { exportXlsx } from '../../../lib/xlsxExport';
import {
  ExportButtons, LoadError, Spinner, Tile, controlsCls, fmtCur, fmtCur2, isoDaysAgo, labelCls, pill,
  tableWrap, tdCls, tdR, thCls, thR,
} from './apShared';

const FLAG_LABEL: Record<QboDiffFlag, string> = {
  onlyAdvantage: 'Only in Advantage',
  onlyQbo: 'Only in QuickBooks',
  amountDiff: 'Amount differs',
  balanceDiff: 'Open balance differs',
  dateDiff: 'Date differs',
  partyDiff: 'Vendor/customer differs',
};
const FLAG_CLS: Record<QboDiffFlag, string> = {
  onlyAdvantage: 'bg-red-500/15 text-red-300',
  onlyQbo: 'bg-orange-500/15 text-orange-300',
  amountDiff: 'bg-red-500/15 text-red-300',
  balanceDiff: 'bg-amber-500/15 text-amber-300',
  dateDiff: 'bg-slate-500/20 text-slate-300',
  partyDiff: 'bg-violet-500/15 text-violet-300',
};
const MATCHED_BY: Record<string, string> = {
  syncLog: 'sync log', billNumber: 'bill #', vendorInvoice: 'vendor inv #', po: 'PO',
  invoiceNumber: 'invoice #', amountAndDate: 'amount + date',
};

const COLS: CsvColumns<QboDiffRow> = [
  ['Differences', r => r.flags.map(f => FLAG_LABEL[f]).join('; ')],
  ['Matched by', r => MATCHED_BY[r.matchedBy] ?? r.matchedBy],
  ['Advantage #', r => r.advantage?.docNo ?? ''], ['Vendor inv # / PO', r => [r.advantage?.ref, r.advantage?.po].filter(Boolean).join(' / ')],
  ['Advantage name', r => r.advantage?.name ?? ''], ['Advantage date', r => r.advantage?.date ?? ''],
  ['Advantage total', r => r.advantage?.total ?? ''], ['Advantage open', r => r.advantage?.balance ?? ''],
  ['QuickBooks #', r => r.qbo?.docNo ?? ''], ['QuickBooks type', r => r.qbo?.type ?? ''], ['QuickBooks name', r => r.qbo?.name ?? ''],
  ['QuickBooks date', r => r.qbo?.date ?? ''], ['QuickBooks total', r => r.qbo?.total ?? ''], ['QuickBooks open', r => r.qbo?.balance ?? ''],
  ['Amount difference', r => r.deltaAmount ?? ''], ['Open difference', r => r.deltaBalance ?? ''],
];

/** The agent's own message when QuickBooks Online is off or not connected on this tenant. */
export function qboOff(err: unknown): string | null {
  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? '';
  if (/not enabled/i.test(msg)) return 'QuickBooks Online is not set up on this tenant.';
  if (/not authenticated|realm/i.test(msg)) return `QuickBooks Online isn't connected: ${msg}`;
  return null;
}

export function QboDiffTab({ defaultEntity = 'bills' }: { defaultEntity?: 'bills' | 'invoices' }) {
  const [entity, setEntity] = useState<'bills' | 'invoices'>(defaultEntity);
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => isoDaysAgo(0));
  const [flag, setFlag] = useState<QboDiffFlag | 'all'>('all');
  const [includeMatched, setIncludeMatched] = useState(false);
  const [search, setSearch] = useState('');

  const q = useQuery({
    queryKey: ['qboDiff', entity, from, to, includeMatched],
    queryFn: () => api.getQboDiff({ entity, from, to, includeMatched }),
    staleTime: 60_000,
    retry: 0,
  });
  const data = q.data;
  const isBills = entity === 'bills';

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (data?.rows ?? []).filter(r => {
      if (flag !== 'all' && !r.flags.includes(flag)) return false;
      if (!s) return true;
      return [r.advantage?.docNo, r.advantage?.ref, r.advantage?.po, r.advantage?.name, r.qbo?.docNo, r.qbo?.name]
        .some(x => (x ?? '').toLowerCase().includes(s));
    });
  }, [data, flag, search]);

  const c = data?.counts;
  const chips: Array<[QboDiffFlag | 'all', string, number | undefined]> = [
    ['all', 'All differences', undefined],
    ['onlyAdvantage', FLAG_LABEL.onlyAdvantage, c?.onlyAdvantage],
    ['onlyQbo', FLAG_LABEL.onlyQbo, c?.onlyQbo],
    ['amountDiff', FLAG_LABEL.amountDiff, c?.amountDiff],
    ['balanceDiff', FLAG_LABEL.balanceDiff, c?.balanceDiff],
    ['dateDiff', FLAG_LABEL.dateDiff, c?.dateDiff],
    ['partyDiff', FLAG_LABEL.partyDiff, c?.partyDiff],
  ];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><GitCompare size={20} /> Advantage vs QuickBooks</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Every {isBills ? 'bill' : 'invoice'} in Advantage next to QuickBooks Online — what's missing on either side and what doesn't agree
        </p>
      </div>

      <div className={controlsCls}>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Compare</span>
          <div className="flex gap-1">
            <button className={pill(isBills)} onClick={() => setEntity('bills')}>Bills</button>
            <button className={pill(!isBills)} onClick={() => setEntity('invoices')}>Invoices</button>
          </div>
        </div>
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Search</span>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Doc #, name…"
              className="pl-6 pr-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white w-48 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-300 pb-1.5">
          <input type="checkbox" checked={includeMatched} onChange={e => setIncludeMatched(e.target.checked)} /> Show matching too
        </label>
        <div className="flex-1" />
        <ExportButtons what="the comparison" disabled={!rows.length}
          onCsv={() => exportCsv(`qbo-diff-${entity}`, COLS, rows)} onXlsx={() => exportXlsx(`qbo-diff-${entity}`, COLS, rows)} />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (qboOff(q.error)
        ? <div className="p-4 rounded-md bg-slate-800/40 border border-slate-700/40 text-slate-300 text-sm">{qboOff(q.error)}</div>
        : <LoadError error={q.error} what="the QuickBooks comparison" />)}

      {q.isLoading ? <Spinner /> : !q.error && data && c && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Tile label="In Advantage" value={c.advantage} sub={fmtCur(data.totals.advantage)} />
            <Tile label="In QuickBooks" value={c.qbo} sub={fmtCur(data.totals.qbo)} />
            <Tile tone="emerald" label="Agree" value={c.same} sub={`of ${c.matched} matched`} />
            <Tile tone={c.onlyAdvantage ? 'red' : 'slate'} label="Missing in QuickBooks" value={c.onlyAdvantage} sub={fmtCur(data.totals.onlyAdvantage)} />
            <Tile tone={c.onlyQbo ? 'amber' : 'slate'} label="Only in QuickBooks" value={c.onlyQbo} sub={fmtCur(data.totals.onlyQbo)} />
            {data.totals.advantageOpen == null
              ? <Tile label="Open in QuickBooks" value={fmtCur(data.totals.qboOpen)} sub="bills are paid in QuickBooks" />
              : <Tile label="Open balance" value={fmtCur(data.totals.advantageOpen)} sub={`QuickBooks ${fmtCur(data.totals.qboOpen)}`} />}
          </div>
          {isBills && c.notSentFlag > 0 && (
            <p className="text-[11px] text-amber-300/80">{c.notSentFlag} bill{c.notSentFlag === 1 ? '' : 's'} in this range still flagged "not sent to QuickBooks" in Advantage.</p>
          )}
          {data.notes.map(n => <p key={n} className="text-[11px] text-slate-500">{n}</p>)}

          <div className="flex flex-wrap gap-1">
            {chips.map(([k, label, n]) => (
              <button key={k} className={pill(flag === k)} onClick={() => setFlag(k)}>
                {label}{n != null && <span className="ml-1 text-slate-400">{n}</span>}
              </button>
            ))}
          </div>

          <div className={`${tableWrap} max-h-[620px]`}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className={thCls}>Difference</th>
                  <th className={thCls}>Advantage #</th><th className={thCls}>Name</th><th className={thCls}>Date</th>
                  <th className={thR}>Total</th><th className={thR}>Open</th>
                  <th className={thCls}>QuickBooks #</th><th className={thCls}>Date</th>
                  <th className={thR}>Total</th><th className={thR}>Open</th>
                  <th className={thCls}>Matched by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const a = r.advantage, b = r.qbo;
                  return (
                    <tr key={`${a?.docNo ?? ''}|${b?.type ?? ''}:${b?.id ?? ''}|${i}`} className="border-t border-slate-800/60 align-top">
                      <td className={tdCls}>
                        <div className="flex flex-wrap gap-1">
                          {r.flags.length === 0 ? <span className="text-emerald-400">Agrees</span>
                            : r.flags.map(f => <span key={f} className={`px-1.5 py-0.5 rounded text-[10px] ${FLAG_CLS[f]}`}>{FLAG_LABEL[f]}</span>)}
                        </div>
                      </td>
                      <td className={tdCls}>
                        {a ? <>{a.docNo}{(a.ref || a.po) && <div className="text-slate-500 text-[10px]">{[a.ref && `inv ${a.ref}`, a.po && a.po !== a.docNo && `PO ${a.po}`].filter(Boolean).join(' · ')}</div>}</> : <span className="text-slate-600">–</span>}
                      </td>
                      <td className={tdCls}>
                        {a?.name ?? b?.name}
                        {r.flags.includes('partyDiff') && b && <div className="text-violet-300 text-[10px]">QB: {b.name}</div>}
                      </td>
                      <td className={tdCls}>{a?.date ?? '–'}</td>
                      <td className={`${tdR} ${r.flags.includes('amountDiff') ? 'text-red-300' : ''}`}>{a ? fmtCur2(a.total) : '–'}</td>
                      <td className={`${tdR} ${r.flags.includes('balanceDiff') ? 'text-amber-300' : ''}`}>{a && !isBills ? fmtCur2(a.balance) : '–'}</td>
                      <td className={tdCls}>{b ? <>{b.docNo || <span className="text-slate-500">(no #)</span>}<div className="text-slate-500 text-[10px]">{b.type}</div></> : <span className="text-slate-600">–</span>}</td>
                      <td className={`${tdCls} ${r.flags.includes('dateDiff') ? 'text-slate-200' : ''}`}>{b?.date ?? '–'}</td>
                      <td className={`${tdR} ${r.flags.includes('amountDiff') ? 'text-red-300' : ''}`}>{b ? fmtCur2(b.total) : '–'}</td>
                      <td className={`${tdR} ${r.flags.includes('balanceDiff') ? 'text-amber-300' : ''}`}>{b ? fmtCur2(b.balance) : '–'}</td>
                      <td className={`${tdCls} text-slate-500`}>{MATCHED_BY[r.matchedBy] ?? r.matchedBy}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-slate-500">{c.same === c.matched && !c.onlyAdvantage && !c.onlyQbo ? 'Everything agrees in this range.' : 'Nothing matches the filter.'}</td></tr>}
              </tbody>
            </table>
          </div>
          {data.truncated && <p className="text-[11px] text-slate-500">Showing the first 5,000 rows — narrow the date range.</p>}
        </>
      )}
    </div>
  );
}
